import { z } from 'zod';
import type { ProgrammingPlanEchelon } from '../ProgrammingPlan/ProgrammingPlanEchelon';
import type { UserPermission } from './UserPermission';

const NationalUserRole = z.enum([
  'AdministratorMaestro',
  'AdministratorBGIR',
  'NationalCoordinator',
  'NationalObserver',
  'LaboratoryUser',
  'LaboratoryOffice'
]);

const RegionalUserRole = z.enum(['RegionalCoordinator', 'RegionalObserver']);
const DepartmentalUserRole = z.enum([
  'DepartmentalCoordinator',
  'DepartmentalObserver'
]);

export const UserRole = z.enum(
  [
    ...NationalUserRole.options,
    ...RegionalUserRole.options,
    ...DepartmentalUserRole.options,
    'Sampler'
  ],
  { error: 'Veuillez renseigner un rôle.' }
);

export type UserRole = z.infer<typeof UserRole>;

export const UserRoleList: UserRole[] = UserRole.options;

const UserSamplerPermissionsList = [
  'viewProgrammingPlans',
  'readProgrammingPlanValidated',
  'readProgrammingPlanClosed',
  'readPrescriptions',
  'downloadSupportDocument',
  'createSample',
  'readSamples',
  'updateSample',
  'performAnalysis',
  'deleteSample',
  'downloadAnalysisRequestDocument',
  'readDocuments',
  'readCompanies',
  'performAnalysis',
  'readAnalysis',
  'deleteSampleDocument',
  'viewDashboard'
] as const satisfies UserPermission[];

const ObserverPermissionsList = [
  'readSamples',
  'downloadSupportDocument',
  'downloadAnalysisRequestDocument',
  'viewProgrammingPlans',
  'readProgrammingPlansInProgress',
  'readProgrammingPlanSubmittedToRegion',
  'readProgrammingPlanValidated',
  'readProgrammingPlanClosed',
  'readPrescriptions',
  'readDocuments',
  'readCompanies',
  'readAnalysis',
  'viewDashboard'
] as const satisfies UserPermission[];

const AdministratorPermissionsList = [
  'administrationMaestro',
  'readSamples',
  'downloadSupportDocument',
  'downloadAnalysisRequestDocument',
  'viewProgrammingPlans',
  'readProgrammingPlansInProgress',
  'readProgrammingPlanSubmittedToRegion',
  'readProgrammingPlanSubmittedToDepartments',
  'readProgrammingPlanValidated',
  'readProgrammingPlanClosed',
  'readPrescriptions',
  'createResource',
  'readDocuments',
  'deleteDocument',
  'readCompanies',
  'readAnalysis',
  'viewDashboard',
  'manageLaboratoryAgreements',
  'manageUsers',
  'manageProgrammingPlanSettings',
  'manageProgrammingPlanNationalCoordinators',
  'readSpecificDataFields'
] as const satisfies UserPermission[];

const userRolePermissions = {
  NationalCoordinator: [
    'manageProgrammingPlan',
    'sendProgrammingPlansToRegions',
    'closeProgrammingPlan',
    'viewProgrammingPlans',
    'readProgrammingPlansInProgress',
    'readProgrammingPlanSubmittedToRegion',
    'readProgrammingPlanSubmittedToDepartments',
    'readProgrammingPlanValidated',
    'readProgrammingPlanClosed',
    'readPrescriptions',
    'downloadSupportDocument',
    'updatePrescription',
    'commentPrescription',
    'readSamples',
    'createResource',
    'readDocuments',
    'deleteDocument',
    'readCompanies',
    'readAnalysis',
    'viewDashboard',
    'exportSamplesWithCodes',
    'manageProgrammingPlanSettings',
    'readSpecificDataFields'
  ],
  RegionalCoordinator: [
    'viewProgrammingPlans',
    'readProgrammingPlanValidated',
    'readProgrammingPlanClosed',
    'readProgrammingPlanSubmittedToRegion',
    'readProgrammingPlanSubmittedToDepartments',
    'approveProgrammingPlan',
    'readPrescriptions',
    'downloadSupportDocument',
    'readSamples',
    'performAnalysis',
    'downloadAnalysisRequestDocument',
    'readDocuments',
    'readCompanies',
    'readAnalysis',
    'viewDashboard',
    'updatePrescriptionLaboratories',
    'commentPrescription',
    'distributePrescriptionToDepartments',
    'manageUsers'
  ],
  NationalObserver: [
    ...ObserverPermissionsList,
    'createResource',
    'deleteDocument'
  ],
  RegionalObserver: ObserverPermissionsList,
  Sampler: UserSamplerPermissionsList,
  DepartmentalCoordinator: [
    'viewProgrammingPlans',
    'readProgrammingPlanValidated',
    'readProgrammingPlanClosed',
    'readPrescriptions',
    'downloadSupportDocument',
    'readSamples',
    'performAnalysis',
    'downloadAnalysisRequestDocument',
    'readDocuments',
    'readCompanies',
    'performAnalysis',
    'readAnalysis',
    'viewDashboard',
    'readProgrammingPlanSubmittedToDepartments',
    'validateProgrammingPlan',
    'updatePrescriptionLaboratories',
    'distributePrescriptionToSlaughterhouses',
    'commentPrescription',
    'manageUsers'
  ],
  DepartmentalObserver: ObserverPermissionsList,
  AdministratorMaestro: [
    ...AdministratorPermissionsList,
    'manageMascarade',
    'manageNotices',
    'manageSpecificDataFields',
    'manageLaboratoryConfig'
  ],
  AdministratorBGIR: [
    ...AdministratorPermissionsList,
    'sendProgrammingPlansToRegions',
    'launchProgrammingPlanCampaign'
  ],
  LaboratoryUser: ['readDocuments', 'readProgrammingPlanValidated'],
  LaboratoryOffice: [
    'readPrescriptions',
    'readProgrammingPlansInProgress',
    'readProgrammingPlanSubmittedToRegion',
    'readProgrammingPlanSubmittedToDepartments',
    'readProgrammingPlanValidated',
    'readProgrammingPlanClosed',
    'manageLaboratoryAgreements'
  ]
} as const satisfies Record<UserRole, UserPermission[]>;

export const UserRolePermissions: Record<UserRole, UserPermission[]> =
  userRolePermissions;

export type UserRoleWithPermission<P extends UserPermission> = {
  [R in UserRole]: P extends (typeof userRolePermissions)[R][number]
    ? R
    : never;
}[UserRole];

export const UserRoleLabels: Record<UserRole, string> = {
  NationalCoordinator: 'Coordinateur national',
  RegionalCoordinator: 'Coordinateur régional',
  NationalObserver: 'Suivi national',
  RegionalObserver: 'Suivi régional',
  DepartmentalCoordinator: 'Coordinateur départemental',
  DepartmentalObserver: 'Suivi départemental',
  Sampler: 'Préleveur',
  AdministratorMaestro: 'Administrateur Maestro',
  AdministratorBGIR: 'Administrateur BGIR',
  LaboratoryUser: 'Laboratoire',
  LaboratoryOffice: 'Bureau des laboratoires'
};

export const UserRoleSorted = [...UserRoleList].sort((a, b) =>
  UserRoleLabels[a].localeCompare(UserRoleLabels[b])
);

export const isNationalRole = (userRole?: UserRole) =>
  NationalUserRole.safeParse(userRole).success;

export const isRegionalRole = (userRole?: UserRole) =>
  RegionalUserRole.safeParse(userRole).success;

export const isDepartmentalRole = (userRole?: UserRole) =>
  //Attention en PPV un préleveur est au niveau régional, donc un préleveur n'est pas un role exclusivement départemental
  DepartmentalUserRole.safeParse(userRole).success;

export const editingEchelonForRole = (
  userRole: UserRole
): ProgrammingPlanEchelon | null => {
  switch (userRole) {
    case 'NationalCoordinator':
    case 'NationalObserver':
      return 'National';
    case 'RegionalCoordinator':
    case 'RegionalObserver':
      return 'Regional';
    case 'DepartmentalCoordinator':
    case 'DepartmentalObserver':
      return 'Departmental';
    default:
      return null;
  }
};

export const seesUnappliedLocalPrescriptionChanges = (
  userRole: UserRole
): boolean =>
  !(['Sampler', 'LaboratoryUser', 'LaboratoryOffice'] as UserRole[]).includes(
    userRole
  );

export type ChangeViewAudience =
  | 'Coordination'
  | 'Departmental'
  | 'Admin'
  | 'Applied';

export const changeViewAudienceForRole = (
  userRole: UserRole
): ChangeViewAudience => {
  if (!seesUnappliedLocalPrescriptionChanges(userRole)) {
    return 'Applied';
  }
  if (
    (['AdministratorMaestro', 'AdministratorBGIR'] as UserRole[]).includes(
      userRole
    )
  ) {
    return 'Admin';
  }
  return editingEchelonForRole(userRole) === 'Departmental'
    ? 'Departmental'
    : 'Coordination';
};

export interface PendingChangeVisibility {
  echelon: ProgrammingPlanEchelon | null;
  seesUnappliedChanges: boolean;
  audience: ChangeViewAudience;
}

export const pendingChangeVisibilityForRole = (
  userRole: UserRole
): PendingChangeVisibility => ({
  echelon: editingEchelonForRole(userRole),
  seesUnappliedChanges: seesUnappliedLocalPrescriptionChanges(userRole),
  audience: changeViewAudienceForRole(userRole)
});
